// Shared template for the six strategic authority pages. Data lives in
// /src/data/authority.js. Route selects the correct entry by slug.
import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { SEO } from "@/data/seo";
import { AUTHORITY_PAGES } from "@/data/authority";
import { GROWTH_PAGES } from "@/data/growthPages";
import { DISCOVERY_PAGES } from "@/data/discoveryPages";
import { Overline, PrimaryButton, SecondaryButton, Crumbs } from "@/components/Primitives";
import CTABlock from "@/components/CTABlock";
import {
  organizationSchema,
  websiteSchema,
  webPageSchema,
  breadcrumbSchema,
  faqPageSchema,
  definedTermSchema,
} from "@/lib/schemas";

// Explicit routing via prop for the 5 known routes; fallback to :slug param.
const AuthorityPage = ({ slug: slugProp }) => {
  const params = useParams();
  const slug = slugProp || params.slug;
  const page = AUTHORITY_PAGES[slug] || GROWTH_PAGES[slug] || DISCOVERY_PAGES[slug];

  const seo = page ? SEO[page.key] || {} : {};
  const canonicalPath = page ? (page.canonicalPath || `/${page.slug}`) : "/";
  const breadcrumbs = page
    ? [
        { name: "Home", path: "/" },
        { name: page.h1, path: canonicalPath },
      ]
    : [];

  useSeo({
    ...seo,
    jsonLd: page
      ? [
          organizationSchema(),
          websiteSchema(),
          webPageSchema({ path: canonicalPath, name: seo.title, description: seo.description }),
          breadcrumbSchema(breadcrumbs),
          faqPageSchema(page.faqs || []),
          page.definedTerm
            ? definedTermSchema({ ...page.definedTerm, path: canonicalPath })
            : null,
        ]
      : [],
  });

  if (!page) return <Navigate to="/" replace />;

  return (
    <main data-testid={`authority-${page.slug.replace(/\//g, "-")}`} className="bg-black text-white">
      <section className="border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-12 lg:py-20">
          <Crumbs items={[{ to: "/", label: "Home" }, { label: page.h1 }]} />
          <Overline className="mt-8">{page.eyebrow}</Overline>
          <h1 className="mt-4 text-5xl sm:text-6xl lg:text-[80px] font-black tracking-tight leading-[0.94] max-w-5xl">
            {page.h1}
          </h1>
          <p className="mt-6 text-base lg:text-xl text-white/75 max-w-3xl leading-relaxed">
            {page.lead}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <PrimaryButton to="/waitlist" testId="authority-primary-cta">Apply to Work With Kostaja</PrimaryButton>
            <SecondaryButton to="/services" testId="authority-secondary-cta">Explore Services</SecondaryButton>
          </div>
        </div>
      </section>

      {page.sections.map((sec) => (
        <section key={sec.h} className="border-b border-white/10">
          <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <h2 className="text-2xl lg:text-4xl font-black tracking-tight leading-[0.95]">{sec.h}</h2>
            </div>
            <div className="lg:col-span-8 space-y-5">
              {(sec.body || []).map((p) => (
                <p key={p.slice(0, 30)} className="text-white/80 text-base lg:text-lg leading-relaxed">{p}</p>
              ))}
              {sec.rows ? (
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/15">
                  {sec.rows.map((r) => (
                    <li key={r.k} className="bg-black p-5 lg:p-6">
                      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#FFC600]">{r.k}</div>
                      <p className="mt-2 text-white/85 text-sm lg:text-base leading-snug">{r.v}</p>
                    </li>
                  ))}
                </ul>
              ) : null}
              {sec.links ? (
                <div className="flex flex-wrap gap-3 pt-2">
                  {sec.links.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-3 border-2 border-white/25 hover:border-[#FFC600] hover:text-[#FFC600] transition-colors"
                    >
                      {l.label} <span aria-hidden>→</span>
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </section>
      ))}

      {page.faqs && page.faqs.length ? (
        <section className="border-b border-white/10">
          <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 lg:py-20">
            <Overline className="mb-4">Frequently asked</Overline>
            <h2 className="text-3xl lg:text-5xl font-black tracking-tight leading-[0.95] mb-10">Common questions</h2>
            <div className="divide-y divide-white/15 border-t border-b border-white/15">
              {page.faqs.map((f) => (
                <details key={f.q} className="group py-6">
                  <summary className="flex items-center justify-between cursor-pointer text-lg lg:text-xl font-black tracking-tight">
                    {f.q}
                    <span aria-hidden className="text-2xl group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-white/75 max-w-3xl leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <CTABlock variant="default" />
    </main>
  );
};

export default AuthorityPage;
